import { useEffect, type ReactNode } from "react";
import { useTheme } from "next-themes";
import { foodEstablishmentJsonLd } from "@/lib/seo";

const LIGHT_VARS: Record<string, string> = {
  "--background": "40 33% 97%",
  "--foreground": "0 0% 16%",
  "--card": "40 33% 97%",
  "--card-foreground": "0 0% 16%",
  "--popover": "40 33% 97%",
  "--popover-foreground": "0 0% 16%",
  "--primary": "95 15% 38%",
  "--primary-foreground": "40 33% 97%",
  "--secondary": "37 32% 77%",
  "--secondary-foreground": "0 0% 16%",
  "--muted": "37 25% 90%",
  "--muted-foreground": "0 0% 40%",
  "--accent": "27 33% 48%",
  "--accent-foreground": "40 33% 97%",
  "--destructive": "0 72% 51%",
  "--destructive-foreground": "40 33% 97%",
  "--border": "37 20% 85%",
  "--input": "37 20% 85%",
  "--ring": "95 15% 38%",
  "--success": "80 30% 65%",
  "--success-foreground": "0 0% 16%",
  "--warning": "38 92% 50%",
  "--warning-foreground": "0 0% 100%",
  "--sidebar-background": "0 0% 16%",
  "--sidebar-foreground": "37 32% 77%",
  "--sidebar-primary": "95 15% 38%",
  "--sidebar-primary-foreground": "40 33% 97%",
  "--sidebar-accent": "0 0% 22%",
  "--sidebar-accent-foreground": "37 25% 90%",
  "--sidebar-border": "0 0% 24%",
  "--sidebar-ring": "95 15% 38%",
};

const style = Object.fromEntries(
  Object.entries(LIGHT_VARS).map(([k, v]) => [k, v])
) as React.CSSProperties;

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * Force light theme on <html> while any public page is mounted.
 *
 * The inline CSS vars on the wrapper div cover the subtree, but portal
 * components (Dialog, Sheet, sonner Toaster) render directly under
 * document.body and inherit the user's OS-preferred .dark class. Stripping
 * it here keeps portals + body background light on customer-facing pages
 * without disabling dark mode on admin routes (restored on unmount).
 */
function ForceLightTheme() {
  const { theme, setTheme } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    const previousTheme = theme;
    root.classList.remove("dark");
    setTheme("light");
    return () => {
      if (hadDark) root.classList.add("dark");
      if (previousTheme) setTheme(previousTheme);
    };
    // Intentionally run once per public-page mount; we capture prior state
    // in closure and restore on unmount. Re-running on theme changes during
    // the public visit would fight the user's own toggle (they can't reach
    // one anyway — no ThemeToggle on public routes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Inject FoodEstablishment JSON-LD once per public page mount.
 * Uses a separate script tag from useDocumentMeta's per-page JSON-LD
 * so both can coexist (page-level Product/Menu + site-wide business info).
 */
function SiteJsonLd() {
  useEffect(() => {
    const id = "site-jsonld";
    if (document.getElementById(id)) return;
    const el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(foodEstablishmentJsonLd());
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);
  return null;
}

export function PublicLayout({ children, className = "" }: Props) {
  return (
    <div style={style} className={className}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-background focus:text-foreground focus:px-3 focus:py-2 focus:rounded focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <ForceLightTheme />
      <SiteJsonLd />
      {children}
    </div>
  );
}
